/**
 * One class keeps a single lead teacher for backwards compatibility and may
 * add Executive co-teachers. Every permission and audience check should use
 * these helpers instead of comparing teacherUid directly.
 */

// Explicit .ts, so node --experimental-strip-types can load this module in a
// test the same way vite loads it in the app.
//
// teacherNameFromSnapshot, not a second reduction helper written here.
// #828 ("The Director assigns a staff title, so Parker can read as Coach
// Parker") already settled the ONE chain a class surface uses to name a
// staff uid from a snapshot, and its policy is show the placeholder,
// never drop. An earlier draft of this file carried its own
// familySafeSnapshotName that did the identical reduction but DROPPED an
// unnameable person instead - two helpers doing one job, with opposite
// answers, is docs/lessons.md's "one fact in two places will drift"
// happening inside a single file. Route through the merged chain instead.
import { hasReadableNameBase, teacherDisplayName, teacherNameFromSnapshot, UNNAMED_PERSON_LABEL } from './teacherTitle.ts';
import { familyAssistantNamesByUid } from './familyAssistantNames.ts';
// #917's dedupe, not a third one written here. "One thing, one place"
// (DESIGN.md 1A.5): oneEntryPerHuman collapses one person's two accounts on
// the identity link the server wrote, dedupedStaffNames adds the staff-only
// last initial when two DIFFERENT humans still share a first name, and
// oneEntryPerFamilyLabel is the label-only collapse a family reader can
// have. jrStaffRoster.ts pulls in no Firebase at load time (it type-imports
// UserDoc for exactly that reason), so this file and the tests that load it
// directly stay import-free too.
import {
  dedupedStaffNames,
  disambiguateSameFirstName,
  oneEntryPerFamilyLabel,
  oneEntryPerHuman,
} from './jrStaffRoster.ts';
import type { UserDoc } from './users.ts';
import { withoutTemporarySubSeats } from './subSeatSchedule.ts';
// Zero imports of its own, so this file stays loadable in a plain node test.
import { teacherNameMatchesUser } from './teacherNameMatch.ts';

// Bobbie asked for unlimited co-teachers on a class (2026-08-30). The studio
// staff list is around 62 people including parent stand-ins, so this feels
// unlimited to her; the number stays finite as a technical guard that keeps
// the offering document sane, not as studio policy.
export const MAX_CO_TEACHERS = 50;

/**
 * WHO A DIRECTOR MAY NAME ON A CLASS'S TEACHING TEAM, lead or additional.
 *
 * Bobbie, 2026-09-01, about the Jr Staff who really run a class: "they need to
 * be able to do everything a teacher can in the class they teach but none of
 * the outside reporting... they don't want them as part of the executive
 * boards." She calls that person a STUDENT TEACHER.
 *
 * The Hub already answers her, and it answers her without a fourth role. Class
 * authority in shared/firestore.rules is granted by BEING NAMED ON THE CLASS,
 * never by holding a role: isAssignedOfferingTeacher(offeringId) is
 * isActiveStaffByDoc() AND membership in the offering's teacherUids, and
 * isActiveStaffByDoc() already includes 'ensemble', which IS the Jr Staff
 * role. Proven on the emulator rather than read, in
 * hub/tests/student-teacher-class-authority-rules.test.mjs: a Jr Staff account
 * named on one class saves that class's family note, music, video and name,
 * writes its staff note, marks and corrects its attendance, posts to its
 * board, records milestones and sources its costumes, and is refused every one
 * of those on a class nobody named them on.
 *
 * So the one thing that ever blocked this was the screen. The lead picker
 * already offered Jr Staff; the additional-teacher picker did not, which is
 * why a Director could never put one alongside an adult teacher. Both pickers
 * ask this function now, so they cannot drift apart again.
 *
 * WHAT THIS IS NOT. Naming somebody here does not change their role, and it
 * hands out nothing studio-wide: the executive board (director, manager,
 * company), the Command Center invite list, the production report and the
 * growth evaluations all gate on ROLE and keep refusing a Jr Staff account
 * whatever classes it teaches. A client `if` is not a permission either way;
 * this decides only who a Director can pick.
 */
export const TEACHING_TEAM_ROLES: readonly string[] = [
  'director',
  'manager',
  'company',
  'ensemble',
];

export interface TeachingTeamAccount {
  role?: string | null;
  status?: string | null;
  /** Mirrors liveUserDoc()'s third condition in shared/firestore.rules. */
  retiredFirebaseDancer?: boolean | null;
}

export function canJoinTeachingTeam(user: TeachingTeamAccount | null | undefined): boolean {
  return user?.status === 'active' && TEACHING_TEAM_ROLES.includes(user.role ?? '');
}

/**
 * The short note the class builder puts beside a name so a Director knows what
 * they are about to hand out. Jr Staff is the only candidate role that reads
 * differently from what a picker labelled "teacher" implies, so it is the only
 * one that gets a note; every other candidate is already an adult teaching
 * account. Staff-only, class builder only: never a family or dancer surface,
 * and never saved into offerings.teacherNames, which is what families read.
 */
export function teachingTeamRoleNote(role: string | null | undefined): string | null {
  return role === 'ensemble' ? 'Jr Staff' : null;
}

export interface OfferingTeacherFields {
  teacherUid?: string | null;
  teacherUids?: readonly string[] | null;
  coTeacherUids?: readonly string[] | null;
  teacherName?: string | null;
  teacherNames?: Record<string, string> | null;
  /** The sub claim lane's temporary-seat ledger. See isOfferingWeeklyTeacher. */
  subSeats?: Readonly<Record<string, unknown>> | null;
}

function cleanUid(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

export function normalizeCoTeacherUids(
  value: unknown,
  leadTeacherUid?: string | null
): string[] {
  if (!Array.isArray(value)) return [];
  const lead = cleanUid(leadTeacherUid);
  if (!lead) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of value) {
    const uid = cleanUid(raw);
    if (!uid || uid === lead || seen.has(uid)) continue;
    seen.add(uid);
    result.push(uid);
    if (result.length >= MAX_CO_TEACHERS) break;
  }
  return result;
}

export function offeringTeacherUids(offering: OfferingTeacherFields | null | undefined): string[] {
  if (!offering) return [];
  const lead = cleanUid(offering.teacherUid);
  const coTeachers = normalizeCoTeacherUids(offering.coTeacherUids, lead);
  return lead ? [lead, ...coTeachers] : [];
}

export function isOfferingTeacher(
  offering: OfferingTeacherFields | null | undefined,
  uid: string | null | undefined
): boolean {
  const clean = cleanUid(uid);
  if (!offering || !clean) return false;
  return offeringTeacherUids(offering).includes(clean);
}

/**
 * Does this class belong on this person's WEEK as a class she teaches?
 *
 * isOfferingTeacher above is the ACCESS question and stays exactly as it is:
 * a sub who picked the class up is a teacher of it for the sub window, so the
 * class page, Teach, attendance and the board all open (DECISIONS.md, "A sub
 * claim opens the class to the claimer from pickup until 24 hours after the
 * class"). This is the SCHEDULE question, and a temporary sub seat is not an
 * answer to it: before this existed, the class a sub covered once sat on her
 * calendar every week of the window (feedback gxpVbdu6). The date she covers
 * comes back through the claimed request instead (lib/subSeatSchedule.ts,
 * coveredClassDateKeys).
 *
 * Use this for anything that repeats the class as hers: a calendar, a "my
 * classes" list, a "you teach N classes" count, tonight's list. Never for a
 * door or a permission.
 */
export function isOfferingWeeklyTeacher(
  offering: OfferingTeacherFields | null | undefined,
  uid: string | null | undefined
): boolean {
  return isOfferingTeacher(withoutTemporarySubSeats(offering), uid);
}

/**
 * WHO TEACHES THIS CLASS EVERY WEEK, lead first: offeringTeacherUids with the
 * temporary sub seats taken off (lib/subSeatSchedule.ts).
 *
 * Bobbie, 2026-10-07 (DECISIONS 123 item 5), on the sub's name on the class
 * card and the schedule grid: "Sub only on that day. The regular teacher
 * shows every week; the sub's name appears only on the covered day." A sub
 * claim seats the claimer on the teaching team for the whole sub window, so
 * every "who teaches this" line read off offeringTeacherUids named her every
 * week of it. Read this for any line that NAMES the teachers of a class
 * without a date; a dated surface that knows the night adds the covering sub
 * back itself (withSubSeatsCoveringOn, or the Sub pill /calendar already
 * draws). Never for a door, an audience or a permission: those still ask
 * offeringTeacherUids, because the sub is a teacher of the class for the
 * window.
 */
export function offeringWeeklyTeacherUids(offering: OfferingTeacherFields | null | undefined): string[] {
  return offeringTeacherUids(withoutTemporarySubSeats(offering));
}

/**
 * THE CLASS'S OTHER TEACHERS EVERY WEEK, the lead left out: the "Also
 * teaching" line on a class card. offeringWeeklyTeacherUids minus the lead,
 * for the same reason (DECISIONS 123 item 5). Refute of PR #1353, round 3:
 * the Classes & dances card built this line off the stored coTeacherUids, so
 * it read "Also teaching: <the sub>" every week of her window, and a viewer
 * who cannot edit the class never sees the Temporary sub cover box that
 * explains it. Never for a door or a permission, same as the weekly list.
 */
export function offeringWeeklyCoTeacherUids(offering: OfferingTeacherFields | null | undefined): string[] {
  const lead = cleanUid(offering?.teacherUid);
  return offeringWeeklyTeacherUids(offering).filter((uid) => uid !== lead);
}

/**
 * THE CLASSES SHE TEACHES EVERY WEEK, counted over EVERY uid she signs in
 * with. A Jr Staff is one person however she signs in (CLAUDE.md, DECISIONS
 * 26 and 27), so a class whose seat sits on her dancer sign-in is one she
 * teaches on her email sign-in too. Callers pass the set from the shared
 * alias helper (lib/jrStaffAliases.ts, jrStaffAliasUidsFor); everyone else
 * passes their one uid and gets exactly isOfferingWeeklyTeacher's answer.
 *
 * THE ONE ANSWER FOR BOTH TEACH AND THE HOME ROW (Bobbie, 2026-10-19, yes to
 * making "You teach N classes" agree with Teach). Teach also matched a class
 * with no teacherUid by its printed teacherName (the seeded schedule writes
 * teacherUid: null and 'Ms. Kim' in teacherName, the 2026-08-05 audit fix),
 * while the home row counted uids only, so the home could say "You teach 4
 * classes" over a Teach list of 9. Both now ask this one function:
 * teacherUid set, the uid match always wins and a name coincidence can
 * never pull in a class assigned to someone else; teacherUid unset, the
 * name match is the fallback (teacherNameMatchesUser, never an email).
 * Leave `displayName` out and only the uid match runs, as before.
 */
export function weeklyTaughtOfferings<T extends OfferingTeacherFields>(
  offerings: readonly T[],
  uids: readonly string[],
  displayName?: string | null,
): T[] {
  const mine = uids.filter((uid) => typeof uid === 'string' && uid.length > 0);
  return offerings.filter((offering) =>
    offering.teacherUid
      ? mine.some((uid) => isOfferingWeeklyTeacher(offering, uid))
      : teacherNameMatchesUser(offering.teacherName, displayName)
  );
}

/**
 * THE NAME AN UNLINKED CLASS IS MATCHED BY FOR THIS VIEWER, or null for a Jr
 * Staff. The one answer Teach's "Everything you teach", the home's "You
 * teach N classes" and the To-dos production reminder all hand to
 * weeklyTaughtOfferings / productionReminderOfferings, so they cannot drift
 * apart again (PR #1381 final review, 2026-10-19: Teach name-matched a Jr
 * Staff while her home row did not).
 *
 * A JR STAFF (role 'ensemble', the same test Landing.tsx uses to mount her
 * JrStaffClassesFirst and useMyJrStaffAliasUids uses to open her alias read)
 * is matched by uid ONLY. Her My classes row (jrStaffHomeClassRows) and her
 * dancer sign-in (dancerPortal.mjs readAssistingClasses) never name-match,
 * and a display name differs between her two sign-ins, so a name match would
 * hand "Ella" on one sign-in a class "Ella B" on the other never sees. A Jr
 * Staff is one person however she signs in (DECISIONS 26, 27).
 *
 * Every other role keeps the printed-name fallback (the 2026-08-05 audit
 * fix): the Firestore user doc's displayName first, then the Auth profile's.
 */
export function taughtClassMatchName(
  userDoc: { role?: string | null; displayName?: string | null } | null | undefined,
  authUser?: { displayName?: string | null } | null,
): string | null {
  if (userDoc?.role === 'ensemble') return null;
  return userDoc?.displayName || authUser?.displayName || null;
}

/**
 * isOfferingTeacher over EVERY uid one person signs in with: the ACCESS
 * question (temporary sub seats count, exactly as isOfferingTeacher counts
 * them), asked of each sign-in the shared alias helper proved is hers
 * (lib/jrStaffAliases.ts, jrStaffAliasUidsFor). A Jr Staff is one person
 * however she signs in (DECISIONS 26), so a class seated on her dancer
 * sign-in is hers on her email sign-in too. Everyone else passes one uid.
 */
export function isOfferingTeacherOnAnySignIn(
  offering: OfferingTeacherFields | null | undefined,
  uids: readonly string[]
): boolean {
  return uids.some((uid) => isOfferingTeacher(offering, uid));
}

/**
 * THE CLASSES THE PRODUCTION REMINDER NAMES FOR THIS VIEWER, from the
 * performing classes (the caller filters performsInShows first, so the card
 * and the tracker it links to agree about technique classes).
 *
 * A Director sees every one: the reminder on To-dos is her studio-wide view.
 * Everyone else sees the classes they teach, asked over EVERY uid they sign in
 * with (isOfferingTeacherOnAnySignIn), so a Jr Staff's reminder lists the same
 * classes on her dancer sign-in as on her email sign-in (10/12 reconcile,
 * item 6). This is the ACCESS question, not the weekly one, on purpose: a sub
 * who picked a class up owes its production work for the window, and
 * weeklyTaughtOfferings would drop it for every teacher. A class with no
 * teacherUid falls back to its printed teacherName (both seed pipelines wrote
 * teacherUid null with the name in teacherName), the same fallback Teach uses,
 * and none for a Jr Staff: callers pass taughtClassMatchName's answer.
 */
export function productionReminderOfferings<T extends OfferingTeacherFields>(
  performing: readonly T[],
  input: { isDirector: boolean; viewerUids: readonly string[]; displayName?: string | null }
): T[] {
  if (input.isDirector) return [...performing];
  const mine = input.viewerUids.filter((uid) => typeof uid === 'string' && uid.length > 0);
  return performing.filter((offering) =>
    offering.teacherUid
      ? isOfferingTeacherOnAnySignIn(offering, mine)
      : teacherNameMatchesUser(offering.teacherName, input.displayName)
  );
}

/**
 * WHO THE RULES COUNT AS A TEACHER OF THIS CLASS, which is NOT the list
 * offeringTeacherUids() above returns, and the difference has cost a real
 * person a working button.
 *
 * offeringTeacherUids() reads teacherUid plus coTeacherUids. shared/firestore.rules
 * reads teacherUids with a teacherUid fallback, and says why in its own comment:
 * "a stale coTeacherUids entry must not revive access". So a co-teacher named
 * only in coTeacherUids is a teacher to every screen and a stranger to that rule.
 *
 * BE EXACT ABOUT "BOTH RULE FILES", BECAUSE THIS PARAGRAPH WAS NOT AND A
 * SEVENTH ROUND CAUGHT IT. shared/storage.rules has TWO teacher tests, not one.
 * isCostumePhotoTeacher() reads teacherUids with the teacherUid fallback and
 * matches Firestore; isAssignedCostumeTeacher() routes through
 * isCurrentUserOfferingTeacher(), which reads teacherUid OR coTeacherUids, and
 * does NOT. The public costume photo goes through the first, the private
 * supplier screenshot through the second, so ONE lane on ONE screen is gated by
 * two different lists. Before this change that shape got
 * Storage ALLOW and Firestore DENY on a costume photo: the bytes uploaded and
 * the card was then refused the imageUrl write, with nothing on screen to say
 * why.
 *
 * This function is the CLIENT MIRROR OF THE RULES, so a screen can ask the
 * question the rules will actually answer. Use it for any gate whose write the
 * rules re-check by class. offeringTeacherUids() stays as it is because
 * audience and display surfaces all over the app depend on it, and narrowing
 * those without being asked would be a second, unreviewed decision.
 *
 * ONE PLACE THE MIRROR IS DELIBERATELY NOT EXACT, AND IT IS LOOSER THAN THE
 * RULES, NOT SAFER. The first version of this paragraph said "safer" and then
 * four sentences later described the offered-then-denied shape, which is the
 * definition of looser; a second independent round proved the contradiction by
 * running the mirror and both rule files over the same offering. Firestore evaluates get()'s default argument
 * EAGERLY, so on an offering carrying teacherUids but NO teacherUid field at
 * all, `offering.get('teacherUids', [offering.teacherUid])` raises "Property
 * teacherUid is undefined on object" and the write is DENIED, even though the
 * caller is named in teacherUids. This function returns that caller instead.
 * Proven on the emulator, not reasoned: both rule files deny that shape, and so
 * does the version before this change, so it is not a seam this introduced.
 * Rules-written offerings cannot reach the shape (hasOfferingShape dereferences
 * teacherUid), but Admin-SDK writers bypass rules, so it is written down here
 * rather than assumed impossible. A screen using this mirror would offer a
 * control the rules then refuse: the offered-then-denied shape, in the one
 * corner where the mirror cannot follow.
 *
 * THE PRESENCE TEST MATTERS AND IS NOT A ?? DEFAULT. The rules call
 * offering.get('teacherUids', [offering.teacherUid]), which falls back only
 * when the field is ABSENT. An offering carrying an EMPTY teacherUids has no
 * teachers by that rule, and a `?? []` here would have quietly fallen back to
 * the lead and disagreed with the gate again.
 */
export function rulesOfferingTeacherUids(
  offering: OfferingTeacherFields | null | undefined
): string[] {
  if (!offering) return [];
  const named = offering.teacherUids;
  if (Array.isArray(named)) {
    return named.map((raw) => cleanUid(raw)).filter((uid): uid is string => !!uid);
  }
  const lead = cleanUid(offering.teacherUid);
  return lead ? [lead] : [];
}

export function isRulesOfferingTeacher(
  offering: OfferingTeacherFields | null | undefined,
  uid: string | null | undefined
): boolean {
  const clean = cleanUid(uid);
  if (!offering || !clean) return false;
  return rulesOfferingTeacherUids(offering).includes(clean);
}

/**
 * WHO MAY TOUCH A COSTUME CARD ON THIS CLASS. The client mirror of
 * costumeStaff() in shared/firestore.rules and canWriteCostumeImage() in
 * shared/storage.rules, kept in one place so a screen and a rule cannot drift.
 *
 * Bobbie's door, assembled from three answers: "All teachers managers,
 * Director should be able to upload costumes photos... Teacher should only be
 * able to upload it to their own classes. Manager should be able to upload it
 * to any class" (2026-09-20); "Yes, create cards too" for a Manager on any
 * class (2026-09-21); and "Yes, for her own class only" for a Jr Staff who is
 * the named lead teacher of her own class (2026-09-21 evening), which keeps her
 * 2026-09-01 ruling that such a person does "everything a teacher can in the
 * class they teach but none of the outside reporting".
 *
 * FRONT DESK IS OUT, and that is the one thing this narrows. canEditClassDetails
 * on the class page includes foh, so the costume pick form was offered to front
 * desk while both rule files refused an unassigned foh user: offered then
 * denied, the exact drift the comment beside that flag warns about. Asking this
 * function instead of a class-edit flag is what closes it.
 */
/**
 * THE RULES' OWN DIRECTOR TEST, as a client mirror, because this triple was
 * being retyped at each call site and a fourth round counted three copies of it.
 * isDirectorAtRuleCliff() is isActiveUserInRoles(['director']), and liveUserDoc()
 * requires status 'active' AND retiredFirebaseDancer not true. Deliberately NOT
 * the email-bootstrap branch: the costume lanes use isDirectorAtRuleCliff(),
 * which reads the users doc only, so a client that honoured the email would
 * offer controls Firestore refuses.
 */
export function isRulesDirector(account: TeachingTeamAccount | null | undefined): boolean {
  return account?.role === 'director'
    && account.status === 'active'
    && account.retiredFirebaseDancer !== true;
}

export function canTouchCostumeCard(
  account: TeachingTeamAccount | null | undefined,
  offering: OfferingTeacherFields | null | undefined,
  uid: string | null | undefined
): boolean {
  // retiredFirebaseDancer is part of the rules' own liveUserDoc() check, so it
  // belongs here too. It was added to the Remove button first and a third round
  // pointed out that its three siblings, the photo control, Edit details and the
  // whole Submit-pick form, still offered themselves to a retired account that
  // both rule files refuse. One of four controls closed is not a fix.
  if (account?.status !== 'active' || account.retiredFirebaseDancer === true) return false;
  const role = account.role ?? '';
  if (role === 'director' || role === 'manager') return true;
  if (role === 'company' || role === 'ensemble') return isRulesOfferingTeacher(offering, uid);
  return false;
}

/**
 * THE PRIVATE SOURCING LANE, WHICH IS NARROWER THAN THE CARD LANE, and keeping
 * the two apart is what a sixth round found missing.
 *
 * Bobbie's costume-photo ruling widened who may create a costume card and add
 * its family-facing photo: "Manager should be able to upload it to any class."
 * It said nothing about the supplier link, costume code, private note and
 * supplier screenshot, and those were never widened. Both rule files still read
 * director-or-ASSIGNED-TEACHER for them: shared/firestore.rules requires
 * isDirectorAtRuleCliff() || isAssignedOfferingTeacher(offeringId) on
 * costumePrivateDetails, and shared/storage.rules requires isDirector() ||
 * isAssignedCostumeTeacher(costumeId) on costume-private-images.
 *
 * AND THE TWO HALVES OF THAT LANE DO NOT READ THE SAME LIST. Firestore's
 * isAssignedOfferingTeacher() reads teacherUids with a teacherUid fallback;
 * storage.rules' isAssignedCostumeTeacher() routes through
 * isCurrentUserOfferingTeacher(), which reads teacherUid OR coTeacherUids. The
 * first version of this function mirrored Firestore alone and this comment
 * claimed both, which a seventh round disproved on the emulator: a Manager
 * named in teacherUids but not in teacherUid or coTeacherUids got Firestore
 * ALLOW and Storage DENY. Read the save order before trusting any account of
 * what that costs her: the screenshot upload and the private write sit in ONE
 * try block with the upload FIRST, so the DENY throws before upsertPrivateDetails
 * is ever reached and NOTHING private saves, not the supplier link, not the code,
 * not the note. The costume and its family-facing photo have already landed by
 * then, so she is left with a card that reads finished and a private half that
 * is simply not there. The screen is not silent about it, but what it shows is
 * the raw Firebase message naming the costume-private-images path, which is a
 * stack trace rather than an answer.
 *
 * So the mirror is the INTERSECTION: a control only appears when BOTH rule
 * files will accept the write behind it. That is deliberately tighter than
 * Firestore alone. Tighter hides a lane; looser offers a control that fails
 * halfway through and leaves a half-saved card, and half-saved is the failure
 * this whole PR exists to stop. Aligning isAssignedCostumeTeacher() with
 * isCostumePhotoTeacher() would let this widen back out; that is a rules change
 * and it is on Bobbie's list, not assumed here.
 *
 * The Director arm is isDirectorAtRuleCliff(), NOT the email-bootstrap branch,
 * for the same reason isRulesDirector says so. A Manager who IS an assigned
 * teacher of the class gets it. A Manager who is not gets the card and the
 * photo and no sourcing block, which is exactly what the rules do. Whether she
 * SHOULD get it on any class is a rules change and Bobbie's call; do not widen
 * this on inference.
 */
export function canTouchCostumePrivateDetails(
  account: TeachingTeamAccount | null | undefined,
  offering: OfferingTeacherFields | null | undefined,
  uid: string | null | undefined
): boolean {
  if (account?.status !== 'active' || account.retiredFirebaseDancer === true) return false;
  if (isRulesDirector(account)) return true;
  // isActiveStaffByDoc()'s role list, spelled out rather than inferred.
  if (!['director', 'manager', 'company', 'ensemble', 'foh'].includes(account.role ?? '')) return false;
  // Firestore's list AND Storage's list. Both, or the control does not appear.
  return isRulesOfferingTeacher(offering, uid) && isOfferingTeacher(offering, uid);
}

export function offeringHasTeacher(offering: OfferingTeacherFields | null | undefined): boolean {
  return offeringTeacherUids(offering).length > 0;
}

/**
 * A saved name a class line may print, or '' when it cannot name anybody:
 * not a string, blank, invisible characters only (hasReadableNameBase), an
 * address, or a bare title. The saved spelling is returned untouched; this
 * decides only WHETHER a snapshot names someone, never what it says.
 */
function usableSavedTeacherName(value: unknown): string {
  if (typeof value !== 'string') return '';
  const saved = value.trim();
  return hasReadableNameBase(saved) && teacherNameFromSnapshot(saved) !== UNNAMED_PERSON_LABEL ? saved : '';
}

/**
 * ONE TEACHING SEAT'S SAVED NAME: its teacherNames entry, else (for the lead
 * only) the legacy teacherName string, else UNNAMED_PERSON_LABEL. Never a
 * uid and never an address. A real assignment with no usable name is still
 * an assignment, so it reads "Someone" (#828: show the placeholder, never
 * drop) rather than vanishing off the line.
 */
export function offeringTeacherSnapshotName(offering: OfferingTeacherFields, uid: string): string {
  return usableSavedTeacherName(offering.teacherNames?.[uid])
    || (uid === cleanUid(offering.teacherUid) ? usableSavedTeacherName(offering.teacherName) : '')
    || UNNAMED_PERSON_LABEL;
}

/** The schedule name a leadless class carries, when it is readable at all. */
function legacyTeacherNames(offering: OfferingTeacherFields): string[] {
  const legacy = typeof offering.teacherName === 'string' ? offering.teacherName.trim() : '';
  return hasReadableNameBase(legacy) ? [legacy] : [];
}

const NO_PROVEN_ALIASES: ReadonlyMap<string, string> = new Map();

/**
 * Family-safe denormalized names, in lead-then-co-teacher order, of the
 * WEEKLY team: a temporary sub seat is not named here (DECISIONS 123 item 5,
 * see offeringWeeklyTeacherUids). Every caller is a class line with no date
 * on it (the family class list and calendar cards, the Jr Staff and dancer
 * views, the production report), so the regular teacher shows every week and
 * the sub's name never rides along for the whole window.
 *
 * EVERY WEEKLY SEAT IS ON THE LINE. A seat with no usable saved name reads
 * UNNAMED_PERSON_LABEL ("Ms. Kim + Someone"), the #828 policy this file's
 * header names; it used to be filtered out, so an assigned teacher with no
 * saved name read as no teacher at all. A leadless class has no weekly team
 * and names its schedule string only, the same answer offeringHasTeacher and
 * the Unassigned count give.
 */
export function offeringTeacherNames(offering: OfferingTeacherFields | null | undefined): string[] {
  return offeringTeacherNamesThroughAliases(offering, NO_PROVEN_ALIASES);
}

/**
 * offeringTeacherNames with ONE PERSON, ONE ENTRY across uids a caller has
 * PROVEN are one person. `aliases` maps a uid to its person key; a uid it
 * does not hold is its own person, so an empty map is exactly
 * offeringTeacherNames. The map must come from the server-written identity
 * link (the Jr Staff identity projection, jrStaffUidAliases), never from two
 * labels reading alike: two different people who share a first name are two
 * people. The person keeps the position of her first seat.
 *
 * HER NAME IS HER EMAIL ACCOUNT'S SEAT, the same one the dancer portal, the
 * Classes card and the tracker export name her by (Bobbie, 2026-09-02,
 * "lists show the email account"). The map's person key IS that account
 * (the projection's canonicalUid), so its seat answers first and her other
 * seats follow in team order; the first usable snapshot wins. Each title
 * lane rewrites only its own seat's saved name, so taking the lead seat's
 * here named one person two ways depending on the screen (J11 refute round
 * 1, finding 2).
 */
export function offeringTeacherNamesThroughAliases(
  offering: OfferingTeacherFields | null | undefined,
  aliases: ReadonlyMap<string, string>
): string[] {
  if (!offering) return [];
  const uids = offeringWeeklyTeacherUids(offering);
  if (uids.length === 0) return legacyTeacherNames(offering);
  const order: string[] = [];
  const seatsByPerson = new Map<string, string[]>();
  for (const uid of uids) {
    const person = aliases.get(uid) ?? uid;
    const seats = seatsByPerson.get(person);
    if (seats) {
      seats.push(uid);
      continue;
    }
    seatsByPerson.set(person, [uid]);
    order.push(person);
  }
  return order.map((person) => {
    const seats = seatsByPerson.get(person) ?? [];
    const ordered = seats.includes(person) ? [person, ...seats.filter((uid) => uid !== person)] : seats;
    for (const uid of ordered) {
      const name = offeringTeacherSnapshotName(offering, uid);
      if (name !== UNNAMED_PERSON_LABEL) return name;
    }
    return UNNAMED_PERSON_LABEL;
  });
}

export function offeringTeacherLabel(offering: OfferingTeacherFields | null | undefined): string | null {
  const names = offeringTeacherNames(offering);
  return names.length > 0 ? names.join(' + ') : null;
}

/** offeringTeacherLabel through proven aliases; see offeringTeacherNamesThroughAliases. */
export function offeringTeacherLabelThroughAliases(
  offering: OfferingTeacherFields | null | undefined,
  aliases: ReadonlyMap<string, string>
): string | null {
  const names = offeringTeacherNamesThroughAliases(offering, aliases);
  return names.length > 0 ? names.join(' + ') : null;
}

/** One person on a class's weekly teaching team, as a staff list shows her. */
export interface OfferingStaffTeacherRow {
  /** The seat's uid that survived the collapse (her email account when both are on the class). */
  uid: string;
  label: string;
}

/**
 * THE WEEKLY TEAM FOR A STAFF READER WHO HOLDS A DIRECTORY: one row per
 * person, lead first, uid and label together so a filter can never pair one
 * person's uid with the next person's name.
 *
 * #917's collapse, not a new one: oneEntryPerHuman folds one Jr Staff's two
 * sign-ins on the identity link the server wrote (users/{uid}.studentId on
 * her OWN sign-ins, never a guardian's), and dedupedStaffNames spends a last
 * initial only when two DIFFERENT people still share a name. Nothing here
 * compares labels to decide who is who. `users` is the caller's own
 * directory; an empty one is honest (nothing collapses, nothing is lost).
 *
 * The default name is the live account name when the directory holds the
 * account, else the seat's saved snapshot, else "Someone"
 * (offeringTeacherSnapshotName). A caller with its own staff label (the
 * class builder's "(Jr Staff)" note) passes `nameFor`.
 */
export function offeringStaffTeacherRows(
  offering: OfferingTeacherFields | null | undefined,
  users: readonly UserDoc[],
  nameFor?: (uid: string, user: UserDoc | null) => string
): OfferingStaffTeacherRow[] {
  if (!offering) return [];
  const uids = oneEntryPerHuman(offeringWeeklyTeacherUids(offering), users);
  const label = nameFor
    ?? ((uid: string, user: UserDoc | null) => (user ? teacherDisplayName(user) : offeringTeacherSnapshotName(offering, uid)));
  // uids is already one entry per human, so dedupedStaffNames' own collapse
  // keeps every entry and its names stay index-aligned with uids.
  const names = dedupedStaffNames(uids, users, label);
  return uids.map((uid, index) => ({ uid, label: names[index] || UNNAMED_PERSON_LABEL }));
}

/**
 * WHICH OF ONE PERSON'S ROWS NAMES HER OPTION, for a list that gathers her
 * rows from many classes (the Teacher filters on Classes & dances and the
 * Technique command center, which must agree). Higher wins; a tie keeps the
 * row read first, so the answer never depends on more than these two facts:
 *   2: a real name on the row of the uid her person key names, which is
 *      canonicalJrStaffAliasUid's answer (her email account when the screen
 *      can prove the link), so her titled "Ms. Ella" beats a stand-in's bare
 *      "Ella Ford" from her other sign-in;
 *   1: a real name on any other of her rows;
 *   0: Someone, so a seat with no saved name never hides a name another of
 *      her seats carries.
 * It only picks among one person's own rows. It never tells two people apart.
 */
export function teacherOptionLabelRank(row: OfferingStaffTeacherRow, personKey: string): number {
  if (row.label === UNNAMED_PERSON_LABEL) return 0;
  return row.uid === personKey ? 2 : 1;
}

/** A label that already ends in a last initial, before any role note: "Ms. Ella F." or "Ms. Ella F. (Jr Staff)". */
const ENDS_IN_LAST_INITIAL = /\s[A-Z]\.(?:\s*\([^()]*\))?\s*$/;

/**
 * A TEACHER FILTER'S OPTIONS, ONE PER PERSON, from her rows on every class
 * (the Teacher filters on Classes & dances and the Technique command center,
 * which must agree). Each option is labelled from the best of that person's
 * rows (teacherOptionLabelRank), and then ONE pass over the whole list tells
 * apart two different people whose options would read the same: the same
 * disambiguateSameFirstName rule a class's own line uses (dedupedStaffNames),
 * so it spends a last initial only where two rows collide, only when the
 * viewer's directory holds both accounts and their last names, and never on
 * a seat it cannot see. A row that already carries an initial from its own
 * class keeps it rather than growing a second one. Staff surfaces only, like
 * every last initial.
 */
export function teacherFilterOptionsFromRows(
  rowLists: Iterable<readonly OfferingStaffTeacherRow[]>,
  users: readonly UserDoc[],
  personKey: (uid: string) => string
): { key: string; label: string }[] {
  const byPerson = new Map<string, { label: string; rank: number; uid: string }>();
  for (const rows of rowLists) {
    for (const row of rows) {
      // The option's KEY is the person; its text is always one of her rows' labels.
      const key = personKey(row.uid);
      const rank = teacherOptionLabelRank(row, key);
      const held = byPerson.get(key);
      if (!held || rank > held.rank) byPerson.set(key, { label: row.label, rank, uid: row.uid });
    }
  }
  const people = Array.from(byPerson.entries());
  const byUid = new Map(users.map((user) => [user.uid, user]));
  const labels = disambiguateSameFirstName(people.map(([key, held]) => ({
    name: ENDS_IN_LAST_INITIAL.test(held.label) ? '' : held.label,
    user: byUid.get(held.uid) ?? byUid.get(key) ?? null,
  })));
  return people
    .map(([key, held], index) => ({ key, label: labels[index] || held.label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * offeringStaffTeacherRows joined for one line, with the leadless schedule
 * name when the class has no weekly team, or null when it has neither.
 * Staff surfaces only: the last initial dedupedStaffNames may add never
 * belongs on a family or dancer screen.
 */
export function offeringStaffTeacherLabel(
  offering: OfferingTeacherFields | null | undefined,
  users: readonly UserDoc[]
): string | null {
  if (!offering) return null;
  const rows = offeringStaffTeacherRows(offering, users);
  const names = rows.length > 0 ? rows.map((row) => row.label) : legacyTeacherNames(offering);
  return names.length > 0 ? names.join(' + ') : null;
}


/**
 * offeringTeacherLabel for a FAMILY or DANCER viewer.
 *
 * offeringTeacherNames returns the stored snapshot verbatim, and for a
 * teacher who has not been given a title yet that snapshot is their full
 * name (see teacherNameFromSnapshot in lib/teacherTitle.ts, the #828
 * chain, for the reduction). A family or dancer surface reads this one, so
 * an untitled teacher is "Dallas", never "Dallas Brown" (Bobbie,
 * 2026-08-28). A STAFF surface reads neither this nor the plain label
 * above: since #917 it builds its own names through dedupedStaffNames
 * (lib/jrStaffRoster.ts), which collapses one human's two accounts on the
 * server-written identity link and spends a last initial only when two
 * DIFFERENT people share a first name.
 *
 * NEVER DROPS a teacher whose stored snapshot cannot be reduced to a real
 * name (blank or email-shaped): #828 settled that policy for every staff
 * name surface in the app, and a class quietly losing its teacher line is
 * not gentler than showing UNNAMED_PERSON_LABEL, it just tells the family
 * less than the app knows.
 */
export function offeringFamilyTeacherLabel(
  offering: OfferingTeacherFields | null | undefined
): string | null {
  const names = offeringTeacherNames(offering)
    .flatMap((raw) => raw.split(' + '))
    .map((part) => teacherNameFromSnapshot(part));
  const collapsed = oneEntryPerFamilyLabel(names);
  return collapsed.length > 0 ? collapsed.join(' + ') : null;
}

export interface OfferingAssistantFields {
  assistantUids?: readonly string[] | null;
  assistantNames?: Record<string, string> | null;
  /** Uids whose safe dancer payload labels collide after surnames are removed. */
  assistantDistinctUids?: readonly string[] | null;
}

/** Everything the assistant helpers below need to read a class's staff. */
export type OfferingStaffFields = OfferingAssistantFields & OfferingTeacherFields;

/**
 * Resolves one assistant uid to the name a viewer's own directory has for
 * it, when that viewer has a directory at all. A staff screen passes one; a
 * family or dancer screen does not, because it cannot read a staff account.
 */
export type AssistantNameResolver = (uid: string) => string | null | undefined;

/**
 * The assistant uids a class shows: assistantUids order, each uid once, and
 * never a uid this class already prints as a teacher.
 *
 * THE UID IS THE ONLY IDENTITY FACT THIS LAYER HOLDS, and that is
 * deliberate. An earlier cut of this file also struck out any assistant
 * whose RENDERED NAME matched a teacher's, which reads as tidy and is not:
 * two people who share a first name are two people, so an assistant named
 * Ella on a class taught by a different Ella disappeared from the row
 * entirely, on family surfaces too. Collapsing one human's two accounts is
 * an identity question, and it is answered above this layer, by
 * oneEntryPerHuman (staff, who can read users/{uid}.studentId) or by
 * oneEntryPerFamilyLabel (family and dancer, who can read nothing but the
 * label). Never seed a taken-set with teacher names here.
 */
export function offeringAssistantUids(
  offering: OfferingStaffFields | null | undefined
): string[] {
  if (!offering) return [];
  const teacherUids = new Set(offeringTeacherUids(offering));
  const seen = new Set<string>();
  const uids: string[] = [];
  for (const raw of offering.assistantUids ?? []) {
    const uid = cleanUid(raw);
    if (!uid || seen.has(uid) || teacherUids.has(uid)) continue;
    seen.add(uid);
    uids.push(uid);
  }
  return uids;
}

/**
 * The family-safe name for one assistant uid: the live account name when the
 * viewer has a directory to look in, else the offering's own snapshot, then
 * the honest placeholder. Reduced through teacherNameFromSnapshot either
 * way, so a fresher live name is never a way around the first-name rule.
 */
function assistantName(
  offering: OfferingStaffFields,
  uid: string,
  liveName?: string | null
): string {
  const stored = liveName?.trim() || offering.assistantNames?.[uid]?.trim() || '';
  return teacherNameFromSnapshot(stored);
}

/**
 * The Jr assistants a class shows to a FAMILY or a DANCER.
 *
 * FAMILY-SAFE BY CONSTRUCTION, and this is the whole reason the reduction
 * lives in here rather than at each call site: offering.assistantNames is
 * written by assistantNamesForSave (lib/jrStaffRoster.ts), which falls back
 * to a person's RAW display name whenever they have not been given a title
 * yet. Rendered as stored, an untitled Jr assistant publishes their surname
 * to every family and every dancer who can see the class. Bobbie,
 * 2026-08-28: "on everything teachers only want first name not last for the
 * website and in the app. So it is Ms Dallas not Dallas Brown anywhere."
 * A Jr assistant whose stored snapshot cannot be reduced to a real name
 * shows UNNAMED_PERSON_LABEL rather than vanishing, the #828
 * placeholder-not-drop policy every other staff name surface follows.
 *
 * ONE ENTRY PER RENDERED LABEL was the older behavior. R45 reverses that
 * only for case (c), two different accounts with raw names that reduce to
 * the same label: spend one surname initial so both remain visible. The
 * snapshot still cannot prove whether two uids are one human with changed
 * names. Exact and already-reduced duplicate snapshots remain collapsed;
 * a maiden/married pair with unrelated surnames is indistinguishable here
 * from two different people and remains the separate case (b) identity gap.
 *
 * WHERE THIS APPLIES: the family calendar, family classes, the shared class
 * page, and the dancer pages. Dancer payloads first collapse accounts with
 * the same server-readable studentId and only then remove full surnames.
 *
 * FOUR CASES, retained so the boundary cannot disappear again:
 *
 *   (a) one human who LEADS under one uid and ASSISTS under another is an
 *       identity question above this label-only layer;
 *   (b) one human listed twice under two labels needs an identity fact this
 *       family snapshot does not carry;
 *   (c) two DIFFERENT people sharing a first name now both render with a
 *       surname initial when one is available;
 *   (d) unnamed assistants still collapse to one honest Someone.
 *
 * A Director needs the real headcount and gets it from
 * offeringStaffAssistantNames below, which uses readable user identities.
 */
export function offeringFamilyAssistantNames(
  offering: OfferingStaffFields | null | undefined
): string[] {
  if (!offering) return [];
  const uids = offeringAssistantUids(offering);
  const names = familyAssistantNamesByUid(uids, offering.assistantNames ?? {});
  const distinctUids = new Set(offering.assistantDistinctUids ?? []);
  // Different full snapshots with the same initial still represent separate
  // entries. Deduplicate only identical snapshots, retaining the prior unnamed
  // and split-account behavior without erasing an Ella R. beside another Ella R.
  const seen = new Set<string>();
  return uids.flatMap((uid) => {
    const raw = offering.assistantNames?.[uid]?.trim() ?? '';
    const normalizedRaw = raw.toLocaleLowerCase();
    const key = names[uid] === UNNAMED_PERSON_LABEL ? UNNAMED_PERSON_LABEL
      : distinctUids.has(uid) ? `uid:${uid}` : `${names[uid].toLocaleLowerCase()}|${normalizedRaw}`;
    if (seen.has(key)) return [];
    seen.add(key);
    return [names[uid]];
  });
}

/** "Jr: Name One, Name Two" for a family or dancer, or null when there are none. */
export function offeringFamilyAssistantLabel(
  offering: OfferingStaffFields | null | undefined
): string | null {
  const names = offeringFamilyAssistantNames(offering);
  return names.length > 0 ? `Jr: ${names.join(', ')}` : null;
}

/**
 * The Jr assistants a class shows to STAFF.
 *
 * Same family-safe names, a different dedupe: a Director staffing a class
 * has to see the real headcount, so #917's dedupedStaffNames collapses only
 * on the identity link the server wrote (users/{uid}.studentId) and then
 * spends a last initial when two DIFFERENT humans still share a first name.
 * Two Jr Staff both called Ella stay two entries, "Ms. Ella R." and "Ms.
 * Ella T.", instead of quietly becoming one.
 *
 * `users` is the viewer's own staff directory (useUsersList), and it is both
 * halves of the answer: dedupedStaffNames collapses on it, and the live
 * account name is preferred over a possibly stale snapshot from it, so a
 * caller with no name map of its own passes nothing but the directory.
 * `resolveLiveName` is for a caller that already builds its own labels
 * (the Command Center's userLabel, ClassLevels' teacherNames map) and wants
 * one precedence, not two. Passing an empty directory is honest rather than
 * broken: nothing collapses and nothing is lost, which is what a viewer who
 * cannot read accounts should see.
 */
export function offeringStaffAssistantNames(
  offering: OfferingStaffFields | null | undefined,
  users: readonly UserDoc[],
  resolveLiveName?: AssistantNameResolver
): string[] {
  if (!offering) return [];
  return dedupedStaffNames(
    offeringAssistantUids(offering),
    users,
    (uid, user) => assistantName(
      offering,
      uid,
      resolveLiveName?.(uid) ?? (user ? teacherDisplayName(user) : null)
    )
  ).filter(Boolean);
}

/** "Jr: Name One, Name Two" for a staff viewer, or null when there are none. */
export function offeringStaffAssistantLabel(
  offering: OfferingStaffFields | null | undefined,
  users: readonly UserDoc[],
  resolveLiveName?: AssistantNameResolver
): string | null {
  const names = offeringStaffAssistantNames(offering, users, resolveLiveName);
  return names.length > 0 ? `Jr: ${names.join(', ')}` : null;
}

/**
 * The same list with SURNAMES KEPT, for the three staff-only screens Bobbie
 * exempted from the first-name rule on 2026-08-28: "Keep full names on all
 * three" (Payroll, the Audit Log, printed class rosters). Payroll is money,
 * the audit log is accountability, and a printed roster is what a teacher
 * carries in an emergency.
 *
 * One human is still printed once, on the identity link and never on the
 * name: oneEntryPerHuman is the same collapse dedupedStaffNames runs above,
 * without the first-name disambiguator, because a full name already tells
 * two people who share a first name apart.
 *
 * Deliberately long and awkward to type. Nothing a family or a dancer can
 * open may call it; if you are unsure which you are writing, you want
 * offeringFamilyAssistantNames above.
 */
export function offeringAssistantNamesWithSurnames(
  offering: OfferingStaffFields | null | undefined,
  users: readonly UserDoc[],
  resolveLiveName?: AssistantNameResolver
): string[] {
  if (!offering) return [];
  return oneEntryPerHuman(offeringAssistantUids(offering), users)
    .map((uid) => resolveLiveName?.(uid)?.trim() || offering.assistantNames?.[uid]?.trim() || '')
    .filter(Boolean);
}
